import { createFileRoute, Link } from "@tanstack/react-router";
import clsx from "clsx";
import { ArrowLeft, Send } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { useTicketQuery } from "@/api/support/useTicketQuery";
import { useTicketReplyMutation } from "@/api/support/useTicketReplyMutation";
import LoadingIcon from "@/components/feedback/LoadingIcon";
import { BasicLayout } from "@/components/layout/BasicLayout";
import { useFloatingPanel } from "@/components/chat/useFloatingPanel";

const categoryLabels: Record<string, string> = {
  bug: "Usterka",
  payment: "Płatność",
  idea: "Pomysł",
};

const statusLabels: Record<string, string> = {
  new: "Nowe",
  in_progress: "W trakcie",
  resolved: "Rozwiązane",
  closed: "Zamknięte",
};

const statusColors: Record<string, string> = {
  new: "bg-accent/10 text-accent",
  in_progress: "bg-amber-500/10 text-amber-500",
  resolved: "bg-accent/10 text-accent",
  closed: "bg-surface-hover text-tertiary",
};

export const Route = createFileRoute("/_auth/support/$ticketId")({
  component: TicketDetailPage,
});

function ReplyPanel({
  ticketId,
}: {
  ticketId: string;
}) {
  const replyMutation = useTicketReplyMutation();
  const [text, setText] = useState("");
  const { panelRef } = useFloatingPanel({});

  const handleSubmit = () => {
    if (!text.trim() || replyMutation.isPending) return;
    replyMutation.mutate(
      { ticketId, content: text.trim() },
      { onSuccess: () => setText("") },
    );
  };

  return (
    <div className="absolute inset-x-0 bottom-0 pb-safe px-3 md:px-6 lg:px-8 z-20" ref={panelRef}>
      <div className="mx-auto max-w-2xl bg-panel backdrop-blur-3xl border border-accent/15 rounded-2xl shadow-xl shadow-accent/5 p-3">
        <div className="bg-surface/80 border border-border rounded-xl overflow-hidden focus-within:border-accent/40 transition-all mb-2">
          <textarea
            className="w-full px-3.5 py-2.5 text-sm text-primary bg-transparent placeholder:text-tertiary/50 outline-none resize-none"
            rows={2}
            placeholder="Napisz wiadomość..."
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
          />
        </div>
        <button
          type="button"
          disabled={!text.trim() || replyMutation.isPending}
          onClick={handleSubmit}
          className="w-full flex items-center justify-center gap-2 rounded-xl bg-accent py-2.5 text-sm font-medium text-white shadow-glow hover:shadow-glow-strong disabled:opacity-40 disabled:shadow-none transition-all cursor-pointer"
        >
          {replyMutation.isPending ? (
            <LoadingIcon className="size-4" />
          ) : (
            <Send className="size-4" />
          )}
          {replyMutation.isPending ? "Wysyłanie..." : "Wyślij"}
        </button>
      </div>
    </div>
  );
}

function TicketDetailPage() {
  const { ticketId } = Route.useParams();
  const { data, isPending } = useTicketQuery(ticketId);
  const bottomRef = useRef<HTMLDivElement>(null);

  const ticket = data?.ticket;
  const isClosed = ticket?.status === "closed";

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [ticket?.messages?.length]);

  return (
    <BasicLayout title="Pomoc">
      <div className="h-full flex flex-col relative">
        {isPending ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="relative size-8">
              <div className="absolute inset-0 rounded-full border-2 border-border" />
              <div className="absolute inset-0 rounded-full border-2 border-accent border-t-transparent animate-spin" />
            </div>
          </div>
        ) : ticket ? (
          <>
            {/* ─── Ticket header ─── */}
            <div className="shrink-0 border-b border-border">
              <div className="max-w-2xl mx-auto px-4 sm:px-6 py-3">
                <div className="flex items-center gap-3 mb-2">
                  <Link
                    to="/support"
                    className="p-1 -ml-1 rounded-lg text-tertiary hover:text-secondary hover:bg-surface-hover transition-colors"
                  >
                    <ArrowLeft className="size-4" />
                  </Link>
                  <h2 className="text-sm font-semibold text-primary truncate flex-1">
                    {ticket.subject}
                  </h2>
                  <span
                    className={clsx(
                      "inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-medium shrink-0",
                      statusColors[ticket.status] ?? "",
                    )}
                  >
                    {statusLabels[ticket.status] ?? ticket.status}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-[10px] text-tertiary ml-7">
                  <span>{categoryLabels[ticket.category] ?? ticket.category}</span>
                  <span>·</span>
                  <span className="tabular-nums">
                    {new Date(ticket.createdAt).toLocaleDateString("pl-PL", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                    })}
                  </span>
                </div>
              </div>
            </div>

            {/* ─── Messages ─── */}
            <div className="flex-1 overflow-y-auto">
              <div className={clsx(
                "max-w-2xl mx-auto px-4 sm:px-6 py-6 space-y-3",
                !isClosed && "pb-32",
              )}>
                {ticket.messages.map(
                  (msg: {
                    id: string;
                    content: string;
                    isAdmin: boolean;
                    createdAt: string;
                  }) => (
                    <div
                      key={msg.id}
                      className={clsx(
                        "flex",
                        msg.isAdmin ? "justify-start" : "justify-end",
                      )}
                    >
                      <div
                        className={clsx(
                          "max-w-[80%] sm:max-w-[70%] rounded-2xl px-4 py-2.5",
                          msg.isAdmin
                            ? "bg-surface border border-border rounded-tl-sm border-l-2 border-l-accent/20"
                            : "bg-user-bubble border border-border-user-bubble rounded-tr-sm",
                        )}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span
                            className={clsx(
                              "text-[10px] font-semibold",
                              msg.isAdmin ? "text-accent/70" : "text-tertiary",
                            )}
                          >
                            {msg.isAdmin ? "Wsparcie" : "Ty"}
                          </span>
                          <span className="text-[10px] text-tertiary tabular-nums">
                            {new Date(msg.createdAt).toLocaleString("pl-PL", {
                              day: "2-digit",
                              month: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        </div>
                        <p className="text-sm text-primary whitespace-pre-wrap leading-relaxed">
                          {msg.content}
                        </p>
                      </div>
                    </div>
                  ),
                )}
                <div ref={bottomRef} />
              </div>
            </div>

            {/* ─── Reply panel or closed notice ─── */}
            {isClosed ? (
              <div className="shrink-0 border-t border-border px-4 py-3">
                <p className="text-center text-xs text-tertiary">
                  To zgłoszenie zostało zamknięte.
                </p>
              </div>
            ) : (
              <ReplyPanel ticketId={ticketId} />
            )}
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-tertiary text-sm">
            Nie znaleziono zgłoszenia
          </div>
        )}
      </div>
    </BasicLayout>
  );
}
